import type { HTMLAttributes, ReactNode } from "react";

/**
 * Pílula semântica (etiqueta, prioridade, status, contexto).
 *
 * `dot` liga uma marca de forma além da cor. Estado nunca pode depender só de
 * cor (AGENTS §11), então quem usa a pílula para prioridade, ganho, perda ou
 * reativação DEVE manter o texto e, quando o texto for curto demais para
 * distinguir, ligar o ponto.
 */
export type PillTone = "neutral" | "accent" | "info" | "good" | "warn" | "crit";

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  dot?: boolean;
  children: ReactNode;
}

export function Pill({ tone = "neutral", dot = false, className, children, ...rest }: PillProps) {
  return (
    <span
      {...rest}
      className={className ? `cx-pill ${className}` : "cx-pill"}
      data-tone={tone}
    >
      {dot && <span className="cx-pill-dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

export interface CountProps extends HTMLAttributes<HTMLSpanElement> {
  value: number;
  /** Rótulo do que está sendo contado, para o leitor de tela. */
  label?: string;
}

/** Contador de dimensão fixa; não altera a altura da linha que o contém (§13.1). */
export function Count({ value, label, className, ...rest }: CountProps) {
  return (
    <span {...rest} className={className ? `cx-count ${className}` : "cx-count"}>
      <span aria-hidden={label ? "true" : undefined}>{value}</span>
      {label && <span className="cx-sr-only">{`${value} ${label}`}</span>}
    </span>
  );
}
